import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  closestCenter,
  CollisionDetection,
  DragOverlay,
  DndContext,
  DropAnimation,
  KeyboardSensor,
  KeyboardCoordinateGetter,
  Modifiers,
  MouseSensor,
  MeasuringConfiguration,
  PointerActivationConstraint,
  ScreenReaderInstructions,
  TouchSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
  defaultDropAnimationSideEffects,
} from '@dnd-kit/core';
import {
  arrayMove,
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
  SortingStrategy,
  rectSortingStrategy,
  AnimateLayoutChanges,
  NewIndexGetter,
} from '@dnd-kit/sortable';

import { SortableItem as Item } from './item';
import { Container as List } from './container';

export interface Props<T extends { id: UniqueIdentifier }> {
  activationConstraint?: PointerActivationConstraint;
  animateLayoutChanges?: AnimateLayoutChanges;
  adjustScale?: boolean;
  collisionDetection?: CollisionDetection;
  coordinateGetter?: KeyboardCoordinateGetter;
  Container?: any;
  dropAnimation?: DropAnimation | null;
  handle?: boolean;
  items: T[];
  measuring?: MeasuringConfiguration;
  modifiers?: Modifiers;
  strategy?: SortingStrategy;
  style?: React.CSSProperties;
  useDragOverlay?: boolean;
  getItemStyles?(args: {
    id: UniqueIdentifier;
    index: number;
    isSorting: boolean;
    isDragOverlay: boolean;
    overIndex: number;
    isDragging: boolean;
  }): React.CSSProperties;
  wrapperStyle?(args: { index: number; isDragging: boolean; id: UniqueIdentifier }): React.CSSProperties;
  isDisabled?(id: UniqueIdentifier): boolean;
  renderItem?(item: T): React.ReactElement;
  onReorder?(items: T[]): void;
}

const dropAnimationConfig: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5',
      },
    },
  }),
};

const screenReaderInstructions: ScreenReaderInstructions = {
  draggable: `
    To pick up a sortable item, press the space bar.
    While sorting, use the arrow keys to move the item.
    Press space again to drop the item in its new position, or press escape to cancel.
  `,
};

export function Sortable<T extends { id: UniqueIdentifier }>({
  activationConstraint,
  animateLayoutChanges,
  adjustScale = false,
  Container = List,
  collisionDetection = closestCenter,
  coordinateGetter = sortableKeyboardCoordinates,
  dropAnimation = dropAnimationConfig,
  getItemStyles = () => ({}),
  handle = false,
  items: initialItems,
  isDisabled = () => false,
  measuring,
  modifiers,
  renderItem,
  strategy = rectSortingStrategy,
  useDragOverlay = true,
  wrapperStyle = () => ({}),
  onReorder,
}: Props<T>) {
  const [items, setItems] = useState<T[]>(() => initialItems);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint,
    }),
    useSensor(TouchSensor, {
      activationConstraint,
    }),
    useSensor(KeyboardSensor, {
      scrollBehavior: 'Cypress' in window ? 'auto' : undefined,
      coordinateGetter,
    })
  );

  const getIndex = (id: UniqueIdentifier) => items.findIndex((item) => item.id === id);

  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  return (
    <DndContext
      sensors={sensors}
      accessibility={{ screenReaderInstructions }}
      collisionDetection={collisionDetection}
      onDragStart={({ active }) => {
        if (!active) return;
        setActiveId(active.id);
      }}
      onDragEnd={({ over }) => {
        setActiveId(null);

        if (over) {
          const overIndex = getIndex(over.id);
          const activeIndex = getIndex(activeId!);

          if (activeIndex !== overIndex) {
            setItems((items) => {
              const newItems = arrayMove(items, activeIndex, overIndex);
              onReorder?.(newItems);
              return newItems;
            });
          }
        }
      }}
      onDragCancel={() => setActiveId(null)}
      measuring={measuring}
      modifiers={modifiers}
    >
      <SortableContext items={items.map((item) => item.id)} strategy={strategy}>
        <Container>
          {items.map((item, index) => (
            <SortableItem
              key={item.id}
              id={item.id}
              //   value={item}
              value={item}
              handle={handle}
              index={index}
              style={getItemStyles}
              wrapperStyle={wrapperStyle}
              disabled={isDisabled(item.id)}
              renderItem={renderItem}
              animateLayoutChanges={animateLayoutChanges}
              useDragOverlay={useDragOverlay}
            />
          ))}
        </Container>
      </SortableContext>
      {useDragOverlay &&
        createPortal(
          <DragOverlay adjustScale={adjustScale} dropAnimation={dropAnimation}>
            {activeId ? (
              <Item
                value={renderItem?.(items[getIndex(activeId)]) ?? items[getIndex(activeId)].id}
                handle={handle}
                wrapperStyle={wrapperStyle({
                  index: getIndex(activeId),
                  isDragging: true,
                  id: activeId,
                })}
                style={getItemStyles({
                  id: activeId,
                  index: getIndex(activeId),
                  isSorting: true,
                  isDragging: true,
                  overIndex: -1,
                  isDragOverlay: true,
                })}
                dragOverlay
              />
            ) : null}
          </DragOverlay>,
          document.body
        )}
    </DndContext>
  );
}

interface SortableItemProps<T extends { id: UniqueIdentifier }> {
  animateLayoutChanges?: AnimateLayoutChanges;
  disabled?: boolean;
  getNewIndex?: NewIndexGetter;
  id: UniqueIdentifier;
  value: T;
  index: number;
  handle: boolean;
  useDragOverlay?: boolean;
  onRemove?(id: UniqueIdentifier): void;
  style(values: any): React.CSSProperties;
  renderItem?: Props<T>['renderItem'];
  wrapperStyle: Props<T>['wrapperStyle'];
}

export function SortableItem<T extends { id: UniqueIdentifier }>({
  disabled,
  animateLayoutChanges,
  getNewIndex,
  handle,
  id,
  index,
  onRemove,
  style,
  value,
  renderItem,
  useDragOverlay,
  wrapperStyle,
}: SortableItemProps<T>) {
  const {
    active,
    attributes,
    isDragging,
    isSorting,
    listeners,
    overIndex,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({
    id,
    animateLayoutChanges,
    disabled,
    getNewIndex,
  });

  return (
    <Item
      ref={setNodeRef}
      value={renderItem?.(value) ?? value.id}
      disabled={disabled}
      dragging={isDragging}
      sorting={isSorting}
      handle={handle}
      handleProps={
        handle
          ? {
              ref: setActivatorNodeRef,
            }
          : undefined
      }
      index={index}
      style={style({
        index,
        id,
        isDragging,
        isSorting,
        overIndex,
      })}
      onRemove={onRemove ? () => onRemove(id) : undefined}
      transform={transform}
      transition={transition}
      wrapperStyle={wrapperStyle?.({ index, isDragging, id })}
      listeners={listeners}
      data-index={index}
      data-id={id}
      dragOverlay={!useDragOverlay && isDragging}
      {...attributes}
    />
  );
}
